import {
    fetchUser,
    setSubmitFeedbackStatus,
    submitFeedback,
} from "actions/user";
import RequireData from "hooks/RequireData";
import SageButton from "kit/SageButton";
import SageErrorAlert from "kit/SageErrorAlert";
import SageScreen from "kit/SageScreen";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import fetchUserStatusSelector from "selectors/fetchUserStatus";
import submitFeedbackStatusSelector from "selectors/submitFeedbackStatus";
import userSelector from "selectors/user";

const Feedback: React.FC = () => {
    const dispatch = useDispatch();
    const [user] = RequireData(
        userSelector,
        fetchUserStatusSelector,
        fetchUser()
    );
    const [email, setEmail] = React.useState("");
    const [feedback, setFeedback] = React.useState("");
    const status = useSelector(submitFeedbackStatusSelector);

    React.useEffect(() => {
        dispatch(setSubmitFeedbackStatus("none"));
    }, [dispatch]);

    React.useEffect(() => {
        if (user) {
            setEmail(user.email);
        }
    }, [user]);

    const onEmailInputChanged = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setEmail(e.target.value);
        },
        [setEmail]
    );

    const onFeedbackChanged = React.useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFeedback(e.target.value);
        },
        [setFeedback]
    );

    const onSubmit = React.useCallback(
        (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            dispatch(submitFeedback(email, feedback));
        },
        [dispatch, email, feedback]
    );

    const onDismissError = React.useCallback(() => {
        dispatch(setSubmitFeedbackStatus("none"));
    }, [dispatch]);

    const errorText = React.useMemo(() => {
        if (status === "error") {
            return "An error occurred. Please try again or reach out to us feedback@rendahand.com";
        } else {
            return null;
        }
    }, [status]);

    return (
        <SageScreen>
            <h2>Feedback</h2>
            <p>
                Have a problem, or experienced a bug using our platform? Submit
                it here.
            </p>
            <form onSubmit={onSubmit}>
                <div className="form-group">
                    <label>Email</label>
                    <input
                        className="form-control"
                        inputMode="email"
                        onChange={onEmailInputChanged}
                        value={email}
                        type="text"
                    />
                </div>
                <div className="form-group">
                    <label>Feedback</label>
                    <textarea
                        value={feedback}
                        onChange={onFeedbackChanged}
                        className="form-control"
                        rows={3}
                    ></textarea>
                </div>
                {status !== "success" && (
                    <SageButton
                        disabled={email.length === 0 || feedback.length === 0}
                        loading={status === "loading"}
                        type="submit"
                        text="Submit Feedback"
                    />
                )}
                {status === "success" && (
                    <p className="text-primary">
                        Success! Your feedback has been recorded, thank you.
                    </p>
                )}
                {errorText && (
                    <SageErrorAlert
                        onDismiss={onDismissError}
                        text={errorText}
                    />
                )}
            </form>
        </SageScreen>
    );
};

export default Feedback;
